<template>
  <div v-if="company.siren && !loading" class="enterprise">
    <div class="col sub-sub-header hide-medium">
      <a :class="{ highlight: hightlight === 'enterprise-general' }" class="link" @click="() => goToAndRemoveAnchor('btn-sub-header', 'enterprise-general')">
        {{ $t('generalInfos') }}
      </a>
      <a
        :class="{ highlight: hightlight === 'enterprise-secondary-etablishment' }"
        class="link"
        @click="() => goToAndRemoveAnchor('etablishment', 'enterprise-secondary-etablishment')"
      >
        {{ $t('secondaryEtablishment') }}
      </a>
      <a
        :class="{ highlight: hightlight === 'enterprise-key-data' }"
        class="link"
        @click="() => goToAndRemoveAnchor('enterprise-key-data', 'enterprise-key-data')"
      >
        {{ $t('keyNumbers') }}
      </a>
    </div>

    <div id="enterprise" class="col enterprise-container">
      <div class="title">{{ $t('generalInfos') }}</div>

      <div class="general-info">
        <div class="line">
          <div class="line-title">{{ $t('companyLabel') }}</div>
          <div class="value">{{ company.denomination || '' }}</div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('brand') }}</div>
          <div class="value">
            <span>{{ company.brandName || company.ensign || $t('unspecifiedF') }}</span>
          </div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('commercialName') }}</div>
          <div class="value">
            <span>{{ company.businessName || $t('unspecified') }}</span>
          </div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('headOffice') }}</div>
          <div class="value">
            {{ company.headOffice && company.headOffice.lines ? company.headOffice.lines.reduce((a, v) => `${a} ${v}`, '') : '' }}
            <div>
              {{ company.headOffice && company.headOffice.zipCode ? company.headOffice.zipCode : '' }}
              {{ company.headOffice && company.headOffice.city ? company.headOffice.city : '' }}
            </div>
          </div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('SIREN') }}</div>
          <div class="value">{{ $utils.sirenFormat(company.siren) }}</div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('legalForm') }}</div>
          <div class="value">{{ company.legalStatus }}</div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('activityNAF') }}</div>
          <div class="value">{{ company.naf ? company.naf + ' :' : '' }} {{ company.activity || $t('unspecifiedActivity') }}</div>
        </div>
        <div class="line">
          <div class="line-title">{{ $t('immatriculationDate') }}</div>
          <div class="value">
            {{ company.registrationDate ? $utils.formatDate(company.registrationDate) : $t('unspecified') }}
          </div>
        </div>
      </div>

      <div id="etablishment" class="title">{{ $t('secondaryEtablishment') }}</div>
      <EstablishmentTable :company="company" />
      <div class="title">Chiffres clés</div>

      <table id="enterprise-key-data" class="table hide-medium reduce" border="0" cellspacing="0" cellpadding="0">
        <thead class="table-header">
          <tr>
            <th class="table-header-item">{{ $t('closureDate') }}</th>
            <th class="table-header-item">{{ $t('ca') }}</th>
            <th class="table-header-item">{{ $t('result') }}</th>
          </tr>
        </thead>
        <tbody class="table-content">
          <tr v-if="!company.keyNumbers" class="table-line">
            <td class="table-item center" colspan="3">{{ $t('notAvailable') }}</td>
          </tr>
          <tr v-for="key in company.keyNumbers" :key="key.id" class="table-line">
            <td class="table-item">
              <div class="item">{{ $utils.formatDate(key.closingDate) }}</div>
            </td>
            <td v-if="key.numbers.salesRevenue !== null" class="table-item">
              <div class="item">{{ setConfidentiality(key, 'salesRevenue') }}</div>
            </td>
            <td v-else class="table-item" colspan="1">
              <div v-if="key.confidential" class="item">{{ $t('accountConfidentiality') }}</div>
              <div v-else class="item">{{ $t('notAvailable') }}</div>
            </td>
            <td v-if="key.numbers.result !== null" class="table-item">
              <div class="item">{{ setConfidentiality(key, 'result') }}</div>
            </td>
            <td v-else class="table-item" colspan="1">
              <div v-if="key.numbers.confidentialResult" class="item">{{ $t('accountResultConfidentiality') }}</div>
              <div v-else class="item">{{ $t('notAvailable') }}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="table-container show-medium">
        <div v-if="!company.keyNumbers" class="line">
          <div class="line-content">
            <div class="item">{{ $t('notAvailable') }}</div>
          </div>
        </div>

        <div v-for="key in company.keyNumbers" :key="key.id" class="table-line">
          <div class="line">
            <div class="line-content">
              <div class="item item-title">{{ $t('closureDate') }}</div>
              <div class="item">{{ $utils.formatDate(key.closingDate) }}</div>
            </div>
          </div>

          <div class="line">
            <div class="line-content">
              <div class="item item-title">{{ $t('ca') }}</div>
              <div class="item">
                {{
                  key.numbers.salesRevenue !== null
                    ? $utils.splitStringBySegmentLength(key.numbers.salesRevenue) + '€'
                    : $t(`secondaryEtablishmentStatusCode._${key.depositIndicator}`)
                }}
              </div>
            </div>
          </div>

          <div class="line">
            <div class="line-content">
              <div class="item item-title">{{ $t('result') }}</div>
              {{
                key.numbers.result !== null
                  ? $utils.splitStringBySegmentLength(key.numbers.result) + '€'
                  : $t(`secondaryEtablishmentStatusCode._${key.depositIndicator}`)
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EstablishmentTable from '@/components/tables/EtablishmentTable';
import throttle from '@/utils/throttle';

export default {
  components: { EstablishmentTable },

  props: {
    company: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      etablishments: [],
      loading: true,
      hightlight: 'enterprise-general',
    };
  },

  computed: {
    ...mapGetters(['user']),

    confidentialAccess() {
      return this.user.client.confidentialAccess;
    },
  },

  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  async mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.loading = false;
  },

  methods: {
    setConfidentiality(key, type) {
      const confidentialText = type === 'result' ? this.$t('accountResultConfidentiality') : this.$t('accountConfidentiality');
      if (!key.numbers[type]) {
        return this.$t('notAvailable');
      } else {
        return (!key.confidential && !key.numbers.confidentialResult) || this.confidentialAccess
          ? `${this.$utils.splitStringBySegmentLength(key.numbers[type])} €`
          : confidentialText;
      }
    },

    goToAndRemoveAnchor(anchor, hightlight) {
      this.hightlight = hightlight;
      const url = location.href;
      location.href = '#' + anchor;
      history.replaceState(null, null, url);
    },

    isInViewport(anchor) {
      const el = document.querySelector(`#${anchor}`);
      if (!document.querySelector(`#${anchor}`)) return;
      return this.$utils.isInViewport(el);
    },

    handleScroll: throttle(function () {
      if (!this) return;
      if (this.isInViewport('enterprise-key-data')) {
        this.hightlight = 'enterprise-key-data';
        if (this.isInViewport('btn-sub-header')) {
          this.hightlight = 'enterprise-general';
          return;
        }
        return;
      }
      if (this.isInViewport('etablishment')) {
        this.hightlight = 'enterprise-secondary-etablishment';
        if (this.isInViewport('btn-sub-header')) {
          this.hightlight = 'enterprise-general';
          return;
        }
        return;
      }
      if (this.isInViewport('btn-sub-header')) {
        this.hightlight = 'enterprise-general';
      }
    }, 50),
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.reduce {
  margin-bottom: 0;
}

.table {
  margin: 0;
}

.table .table-content .table-line {
  .table-item {
    background: colors.$white-color;

    &.center {
      text-align: center;
    }

    .center {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .item {
      color: colors.$darkgrey-color;

      &:not(.black) {
        color: colors.$grey-color !important;
      }
    }
  }

  &:not(:last-child) .table-item {
    border-bottom: 1px solid rgba(colors.$lightgrey-color, 0.3);
  }
}

.enterprise {
  width: 100%;
  display: flex;
  padding: 80px 12px 80px;
  box-sizing: border-box;

  .col {
    &.sub-sub-header {
      flex-grow: 1;
      width: 200px;
      max-height: 200px;
      padding-right: 36px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      position: sticky;
      top: 110px;

      .link {
        margin: 0 0 20px;
        font-weight: 500;
        font-size: 18px;
        color: colors.$grey-color;
        width: 100%;

        &.highlight {
          color: colors.$black-color;
        }

        &:hover {
          cursor: pointer;
          color: rgba(colors.$black-color, 0.8);
        }
      }
    }

    &.enterprise-container {
      display: flex;
      width: 100%;
      padding-left: 80px;
      border-left: 1px solid colors.$lightgrey-color;
      flex-grow: 1;
      flex-direction: column;

      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 20px;
        margin: 60px 0 25px;

        &:first-child {
          margin-top: 0;
        }
      }

      .general-info {
        display: flex;
        flex-direction: column;
        flex-grow: 1;

        .line {
          display: flex;
          height: 48px;

          .line-title {
            font-weight: 500;
            font-size: 15px;
            color: colors.$darkblue-color;
            width: 280px;
          }

          .value {
            width: 280px;
            font-size: 15px;
            color: colors.$grey-color;
            flex-grow: 1;
          }
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .enterprise {
    padding-bottom: 0 !important;
  }

  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        .line-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          margin: 10px 0;

          .item {
            font-size: 14px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: colors.$darkgrey-color;
          }

          .item-title {
            color: colors.$grey-color;
            margin-bottom: 4px;
          }

          .item.bold {
            font-weight: bold;
          }
        }
      }
    }
  }

  .enterprise-container {
    border: none !important;
    padding-left: 0 !important;
  }

  .general-info .line {
    flex-direction: column;
    border-bottom: 1px solid #e1e7ee;
    justify-content: center;

    padding: 8px 0;

    height: 74px !important;

    .line-title {
      font-weight: 500;
      font-size: 14px !important;
      line-height: 16px;

      color: colors.$grey-color !important;

      margin-bottom: 8px;
    }

    .value {
      color: colors.$text-color !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      font-weight: normal;
      font-size: 14px !important;
      line-height: 20px;
    }
  }
}
</style>
